
import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import useScrollToTop from '@/hooks/use-scroll-to-top';

// Define gallery items data structure
interface GalleryItem {
  id: number;
  src: string;
  alt: string;
  category: string;
}

// Gallery items data
const galleryItems: GalleryItem[] = [
  // Tonte de pelouse
  {
    id: 1,
    src: '/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png',
    alt: 'Tonte de pelouse professionnelle',
    category: 'Tonte de pelouse'
  },
  {
    id: 2,
    src: '/lovable-uploads/cb0f1424-4dcd-4096-93b1-ab5efa70c04e.png',
    alt: 'Tonte de pelouse avec lignes parfaites',
    category: 'Tonte de pelouse'
  },
  {
    id: 3,
    src: '/lovable-uploads/36844efb-a60f-4620-88db-3f57be4ded97.png',
    alt: 'Pelouse parfaitement entretenue près d\'une résidence',
    category: 'Tonte de pelouse'
  },
  {
    id: 4,
    src: '/lovable-uploads/86be24b1-a3c0-446c-a703-341cc2add3db.png',
    alt: 'Entretien professionnel de haie',
    category: 'Tonte de pelouse'
  },
  {
    id: 5,
    src: '/lovable-uploads/fd7cb045-f10d-4295-91de-857c8dde7f65.png',
    alt: 'Tonte avec souffleur professionnel',
    category: 'Tonte de pelouse'
  },
  // Ménage d'automne
  {
    id: 6,
    src: '/lovable-uploads/4d4550a3-8c7a-4d2f-a660-b9d322ef85ea.png',
    alt: 'Ménage d\'automne - Cour avec feuilles d\'automne',
    category: 'Ménage d\'automne'
  },
  {
    id: 7,
    src: '/lovable-uploads/7c03396c-100d-4d97-aaeb-911bf929726e.png',
    alt: 'Équipement pour nettoyage d\'automne',
    category: 'Ménage d\'automne'
  },
  {
    id: 8,
    src: '/lovable-uploads/5e5b0657-f995-4b46-a966-62be4b17c0c9.png',
    alt: 'Terrain en nettoyage d\'automne',
    category: 'Ménage d\'automne'
  },
  // Ouverture de terrain
  {
    id: 9,
    src: '/lovable-uploads/b0b1beb8-b76c-40e5-b40a-ff68b25f5cb8.png',
    alt: 'Ouverture de terrain - Nettoyage printanier',
    category: 'Ouverture de terrain'
  },
  {
    id: 10,
    src: '/lovable-uploads/ad3936e3-872f-43e5-842b-2e17aaa65f5d.png',
    alt: 'Travaux d\'aménagement paysager avec des briques',
    category: 'Ouverture de terrain'
  },
  {
    id: 11,
    src: '/lovable-uploads/78ab4870-648c-421d-9942-5e81f7a8e31c.png',
    alt: 'Équipe travaillant sur l\'aménagement d\'un terrain',
    category: 'Ouverture de terrain'
  },
  // Déneigement de toiture
  {
    id: 12,
    src: '/lovable-uploads/7ee3b526-3413-462f-9bcc-9fa2d731a747.png',
    alt: 'Déneigement de toiture',
    category: 'Déneigement de toiture'
  },
  {
    id: 13,
    src: '/lovable-uploads/62bd1a0b-0d2f-4518-ad22-0068ee7a9db4.png',
    alt: 'Déneigement d\'une toiture résidentielle en profondeur',
    category: 'Déneigement de toiture'
  },
  // Terrassement / paysagement
  {
    id: 14,
    src: '/lovable-uploads/e91b0424-144c-4a02-889d-cf40886243e1.png',
    alt: 'Terrassement / paysagement',
    category: 'Terrassement / paysagement'
  },
  {
    id: 15,
    src: '/lovable-uploads/33f83ece-bc20-4a3f-81c1-1b69c7a0fa44.png',
    alt: 'Terrassement / paysagement',
    category: 'Terrassement / paysagement'
  },
  // Nettoyage de stationnement - Adding the new images
  {
    id: 16,
    src: '/lovable-uploads/50bd2caa-82d3-46f1-b7f0-d757c8367a17.png',
    alt: 'Nettoyage de stationnement commercial',
    category: 'Nettoyage de stationnement'
  },
  {
    id: 17,
    src: '/lovable-uploads/204167a9-27e2-447e-a370-c0e963133f50.png',
    alt: 'Stationnement commercial nettoyé professionnellement',
    category: 'Nettoyage de stationnement'
  },
  {
    id: 18,
    src: '/lovable-uploads/0629e7cb-d632-4c60-9893-891ca9cf6f75.png',
    alt: 'Zone industrielle avec stationnement propre',
    category: 'Nettoyage de stationnement'
  },
  {
    id: 19,
    src: '/lovable-uploads/077c0214-479b-43a4-a2d2-6ddb11af4979.png',
    alt: 'Grand stationnement commercial après nettoyage',
    category: 'Nettoyage de stationnement'
  }
];

// All unique categories for filtering
const categories = ['Tous', ...new Set(galleryItems.map(item => item.category))];

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('Tous');
  // Add useScrollToTop hook to ensure page scrolls to top when navigating here
  useScrollToTop();
  
  const filteredItems = activeCategory === 'Tous' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  return (
    <Layout>
      <div className="relative pt-20 pb-16 md:pb-24 bg-brand-green text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png')" }}
          ></div>
        </div>
        <div className="container-wide relative z-10 text-center pt-16 pb-8">
          <h1 className="section-title mb-6">Galerie de projets</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Découvrez nos réalisations et transformations pour des clients résidentiels et commerciaux
          </p>
        </div>
      </div>

      {/* Filter Categories */}
      <div className="bg-brand-stone-light py-8">
        <div className="container-wide">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  activeCategory === category
                    ? 'bg-brand-green text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map(item => (
              <div 
                key={item.id} 
                className="group relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all"
              >
                <AspectRatio ratio={4/3} className="bg-muted">
                  <img 
                    src={item.src} 
                    alt={item.alt} 
                    className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
                    loading="lazy"
                  />
                </AspectRatio>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                  <Badge className="self-start mb-2 bg-brand-green">{item.category}</Badge>
                  <h3 className="text-white font-display text-lg">{item.alt}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GalleryPage;
